import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Overlay } from "@/components/Overlay";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ProcessingScreen } from "@/components/store/ProcessingScreen";

const fmtCard = (v) => v.replace(/\D/g, "").slice(0, 19).replace(/(.{4})/g, "$1 ").trim();
const fmtExp = (v) => { const d = v.replace(/\D/g, "").slice(0, 4); return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d; };
const field = "w-full bg-[#0f131c] border border-[#2a3242] rounded-lg px-3.5 py-3.5 text-[15px] text-white outline-none focus:border-[#3b82f6] transition-colors placeholder:text-[#6b7589]";

const COUNTRIES = ["United States", "Canada", "United Kingdom", "Australia", "Germany", "France", "Spain", "Italy", "Netherlands", "Ireland", "New Zealand", "Mexico", "Brazil", "Japan", "India", "Other"];

export function CheckoutSheet({ pkg, onClose }) {
  const { user, refresh, setSuccess, closeStore } = useApp();
  const cards = user?.cards || [];
  const [f, setF] = useState({ card: "", exp: "", cvc: "", email: "", name: "", country: "United States", zip: "" });
  const [savedId, setSavedId] = useState(cards[0]?.id || null);
  const [useNew, setUseNew] = useState(cards.length === 0);
  const [save, setSave] = useState(true);
  const [busy, setBusy] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => { setError(""); setF((s) => ({ ...s, [k]: v })); };
  if (!pkg) return <ProcessingScreen open={false} />;

  const usingSaved = !useNew && savedId;
  const canPay = usingSaved || (f.card.replace(/\s/g, "").length >= 2 && f.name);

  const pay = async () => {
    setBusy(true); setProcessing(true); setError("");
    try {
      const payload = usingSaved
        ? { package_id: pkg.id, saved_card_id: savedId }
        : { package_id: pkg.id, card_number: f.card, exp: f.exp, cvc: f.cvc, cardholder: f.name, save_card: save };
      const [{ data }] = await Promise.all([api.post("/store/purchase", payload), new Promise((r) => setTimeout(r, 2800))]);
      await refresh();
      onClose(); closeStore();
      setF({ card: "", exp: "", cvc: "", email: "", name: "", country: "United States", zip: "" });
      const now = new Date();
      setSuccess({
        title: "Payment Successful",
        message: `Payment of $${pkg.price.toFixed(2)} was completed.`,
        sub: "Your coins have been delivered to your wallet.",
        okLabel: "DONE — BACK TO GAMES",
        receipt: [
          ["Date", `${now.toLocaleDateString()} · ${now.toLocaleTimeString()}`],
          ["Payment Method", `${data.card_brand} •••• ${data.card_last4}`],
          ["Transaction ID", data.ref],
          ["Gold Coins", `+${data.gc.toLocaleString()} GC`, "gc-text"],
          ["Sweeps Coins", `+${data.sc.toFixed(2)} SC`, "sc-text"],
          ["Status", "✓ COMPLETED · APPROVED", "text-[#c4b5fd]"],
        ],
        ack: `You personally authorized this payment of $${pkg.price.toFixed(2)}, received exactly what was purchased, and understand that Gold Coin purchases are final and non-refundable once coins are credited.`,
      });
    } catch (e) { const m = errMsg(e); setError(m); toast.error(m); }
    finally { setBusy(false); setProcessing(false); }
  };

  return (
    <>
    <ProcessingScreen open={processing} amount={pkg.price.toFixed(2)} />
    <Overlay open={!!pkg} onClose={onClose} testId="checkout-sheet" className="max-w-md" z="z-[55]">
      <div className="bg-[#161b24] rounded-t-3xl sm:rounded-3xl overflow-hidden">
        <div className="p-5 border-b border-[#232a38]">
          <div className="text-sm font-bold text-white">Review your order</div>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FFB800]/15 flex items-center justify-center text-xl">🪙</div>
              <div><div className="font-bold text-white">Gold Coins</div><div className="text-xs text-gray-400">Credits for Sweepstakesgames</div></div>
            </div>
            <div className="font-display font-black text-white" data-testid="checkout-total">USD {pkg.price.toFixed(2)}</div>
          </div>
          <div className="mt-2 text-xs sc-text">{pkg.gc.toLocaleString()} GC + {pkg.sc.toFixed(2)} Free SC</div>
        </div>

        <div className="p-5">
          <div className="text-sm font-bold text-gray-300 mb-3">Pay with</div>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="pay-apple" onClick={pay} disabled={busy} className="rounded-xl bg-white text-black py-3.5 font-bold flex items-center justify-center gap-1 disabled:opacity-60"> Pay</button>
            <button data-testid="pay-google" onClick={pay} disabled={busy} className="rounded-xl bg-white text-[#3c4043] py-3.5 font-bold flex items-center justify-center gap-1 disabled:opacity-60"><span className="text-[#4285f4] font-black">G</span> Pay</button>
          </div>

          {cards.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="saved-cards">
              {cards.map((c) => (
                <button key={c.id} data-testid={`saved-card-${c.id}`} onClick={() => { setSavedId(c.id); setUseNew(false); }} className={`w-full flex items-center justify-between rounded-lg border px-3.5 py-3 text-sm transition-colors ${usingSaved && savedId === c.id ? "border-[#3b82f6] bg-[#3b82f6]/10" : "border-[#2a3242] bg-[#0f131c]"}`}>
                  <span className="font-bold text-white">{c.brand} •••• {c.last4}</span><span className="text-xs text-gray-500">{c.exp || "saved"}</span>
                </button>
              ))}
              <button data-testid="use-new-card-btn" onClick={() => setUseNew(true)} className={`w-full rounded-lg border px-3.5 py-3 text-sm font-bold transition-colors ${useNew ? "border-[#3b82f6] bg-[#3b82f6]/10 text-white" : "border-[#2a3242] bg-[#0f131c] text-gray-300"}`}>+ Use a new card</button>
            </div>
          )}

          {useNew && (
            <>
              <div className="my-4 flex items-center gap-3 text-[11px] font-bold text-gray-500"><span className="flex-1 h-px bg-[#232a38]" />Or<span className="flex-1 h-px bg-[#232a38]" /></div>
              <div className="space-y-3">
                <div className="relative">
                  <input data-testid="checkout-card-number" inputMode="numeric" placeholder="Card number" value={f.card} onChange={(e) => set("card", fmtCard(e.target.value))} className={field} />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1 text-[9px] font-black">
                    <span className="px-1 py-0.5 rounded bg-[#1a1f71] text-white">VISA</span>
                    <span className="px-1 py-0.5 rounded" style={{ background: "linear-gradient(135deg,#eb001b 50%,#f79e1b 50%)", color: "#fff" }}>MC</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="checkout-exp" inputMode="numeric" placeholder="MM/YY" value={f.exp} onChange={(e) => set("exp", fmtExp(e.target.value))} className={field} />
                  <input data-testid="checkout-cvc" inputMode="numeric" placeholder="CVC" value={f.cvc} onChange={(e) => set("cvc", e.target.value.replace(/\D/g, "").slice(0, 6))} className={field} />
                </div>
                <input data-testid="checkout-email" type="email" placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} className={field} />
                <input data-testid="checkout-name" placeholder="Name on card" value={f.name} onChange={(e) => set("name", e.target.value)} className={field} />
                <div className="grid grid-cols-2 gap-3">
                  <select data-testid="checkout-country" value={f.country} onChange={(e) => set("country", e.target.value)} className={field}>
                    {COUNTRIES.map((c) => <option key={c} className="bg-[#0f131c]">{c}</option>)}
                  </select>
                  <input data-testid="checkout-zip" placeholder="Zipcode" value={f.zip} onChange={(e) => set("zip", e.target.value)} className={field} />
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-300"><input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} className="w-4 h-4 accent-[#3b82f6]" data-testid="save-card-checkbox" />Save payment method for future use</label>
              </div>
            </>
          )}

          {error && <div data-testid="checkout-error" className="mt-4 rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 px-3.5 py-3 text-sm font-semibold text-[#FCA5A5]">{error}</div>}
          <button data-testid="checkout-pay-btn" disabled={busy || !canPay} onClick={pay} className="mt-5 w-full rounded-xl bg-[#3b82f6] hover:bg-[#2f6fe0] disabled:opacity-40 py-3.5 font-display text-lg font-bold text-white transition-[background-color,opacity] flex items-center justify-center gap-2">
            {busy && <Loader2 className="animate-spin" size={18} />}Pay
          </button>
          <p className="mt-4 text-center text-[11px] text-gray-500 leading-relaxed">By authorizing the payment above, I agree with Breeze's Terms and Conditions and Privacy Policy.</p>
          <p className="mt-2 text-center text-[11px] text-gray-500">Powered by <span className="font-bold text-gray-300">⊟ breeze</span></p>
        </div>
      </div>
    </Overlay>
    </>
  );
}
